import ArrowDown from "@/Icons/ArrowDown";
import { useTrans } from "@/translations";

import { useMemo } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { FilterTimeFrameValue } from "server/config-api/index.js";
import { twMerge } from "tailwind-merge";

import { Card, CardComponentFilterLocation } from "../card";
import { useFilterSort } from "./FilterSortContext";
import { FilterSortMenu } from "./FilterSortMenu";
import { OPTION_ALL } from "./constants";
import { FilterSort } from "./types";

type CollapsedFilterSortProps = {
  filterSorts: FilterSort[];
} & Pick<React.ComponentProps<typeof Card>, "filterLocation">;

const valuesIgnoredForActiveCount = [
  "",
  OPTION_ALL,
  FilterTimeFrameValue.SinceBeginning,
];

export const CollapsedFilterSortButton: React.FC<CollapsedFilterSortProps> = ({
  filterSorts,
  filterLocation,
}) => {
  const { t } = useTrans();
  const { activeOptions } = useFilterSort();

  const activeCount = useMemo(() => {
    const activeOptionsLength = Object.keys(activeOptions).filter((id) => {
      const filterSort = filterSorts.find((filterSort) => filterSort.id === id);
      return (
        filterSort?.type === "filter" &&
        !activeOptions[id].find((option) =>
          valuesIgnoredForActiveCount.includes(option.value),
        )
      );
    }).length;
    return activeOptionsLength > 0
      ? t("filterLabels:activeCount", { count: activeOptionsLength })
      : "";
  }, [activeOptions, t, filterSorts]);

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            className={twMerge(
              "bg-grey1 flex h-8 items-center gap-2 px-2 py-1 text-lg text-sm",
              open && "bg-grey2",
            )}
          >
            <span className="whitespace-nowrap">
              {t("filterLabels:filters")}
              <span className="pl-1">{activeCount}</span>
            </span>
            <ArrowDown
              className={twMerge(
                "overflow-visible",
                open && "rotate-180 transition-transform",
              )}
            />
          </PopoverButton>
          <PopoverPanel
            className={twMerge(
              "absolute z-20",
              filterLocation === CardComponentFilterLocation.RIGHT && "right-0",
            )}
          >
            <FilterSortMenu
              close={close}
              filterSorts={filterSorts}
              collapsable
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
